/**
 * Shared helper for animation systems to emit dev-mode tracking events.
 *
 * Each animation controller (drag_physics, element_animate_in_on_scroll,
 * mobile_menu, etc.) calls `emitDevModeBind(name, el, extra)` when it
 * binds or fires on an element. The helper:
 *   1. Resolves the element's scope (page / page-overlay / project-
 *      overlay) by walking up the DOM
 *   2. Pulls a stable identifier out (relid / id / first useful class)
 *   3. Emits a `laytheme.emit` event whose args carry the scope token
 *      explicitly — the dev-mode overlay's `inferEventScopes` picks
 *      that up and routes the count into the correct column
 *
 * Cost when dev-mode is OFF: a few string lookups + one `laytheme.emit`
 * with two listeners (the monkey-patch only attaches when dev-mode
 * activates, so non-admins pay just the bare EventEmitter dispatch).
 * Negligible compared to the work the binding itself does.
 */

function detectScopeForElement(el) {
	if (!el || !el.closest) return 'page'
	if (el.closest('#lay-project-overlay')) return 'project_overlay'
	if (el.closest('.lay-overlay')) return 'page_overlay'
	return 'page'
}

function getElementIdentifier(el) {
	if (!el) return ''
	// `data-relid` is the gridder-element identifier — the most useful
	// debugging anchor since you can grep it in the layout JSON.
	const relid = el.getAttribute && (el.getAttribute('data-relid') || el.getAttribute('data-lay-relid'))
	if (relid) return 'relid=' + relid
	if (el.id) return '#' + el.id
	const cls = el.className && typeof el.className === 'string' ? el.className.split(/\s+/).filter(Boolean)[0] : ''
	if (cls) return '.' + cls
	return el.tagName ? el.tagName.toLowerCase() : '?'
}

/**
 * Emit a dev-mode tracking event for an animation/bind operation on an
 * element. Safe to call from anywhere in the frontend — the laytheme
 * event bus is initialised in HEAD before any of our scripts run.
 *
 * The event NAME convention is `<system>:<verb>` — e.g.
 *   - `drag_physics:body_bound`
 *   - `element_animate_in:fired`
 *   - `mobile_menu:opened`
 *
 * Args passed to the emit:
 *   - identifier (relid / id / class)
 *   - scope token (`page` / `project_overlay` / `page_overlay`) — the
 *     dev-mode overlay's scope inference looks at args for these
 *     tokens, so the event auto-routes to the correct column
 *   - optional extra info string (caller-supplied)
 */
export function emitDevModeBind(name, el, extra) {
	if (typeof window === 'undefined' || !window.laytheme || typeof window.laytheme.emit !== 'function') return
	try {
		const scope = detectScopeForElement(el)
		const id = getElementIdentifier(el)
		if (typeof extra === 'string' && extra) {
			window.laytheme.emit(name, id, scope, extra)
		} else {
			window.laytheme.emit(name, id, scope)
		}
	} catch (_) {
		// Never let dev-instrumentation break a binding flow.
	}
}

// Expose globally so non-module JS (legacy frontend files) can call
// this without an import. `window.layDevModeEmit('name', el)` works
// the same as the ESM import.
if (typeof window !== 'undefined') {
	window.layDevModeEmit = emitDevModeBind
}
